import React from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  Box,
  Card,
  CardContent,
  Container,
  Grid,
  FormControlLabel,
  TextField,
  Typography,
  Stack,
  Checkbox,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { useAppSelector } from "../../store";

interface DeclarationFormValues {
  signFullNameFirst: string;
  signDateFirst: string;
  signCheckFirst: boolean;

  signFullNameSecond: string;
  signDateSecond: string;
  signCheckSecond: boolean;
}

const todayString = new Date().toLocaleDateString("en-CA");

const initialValues: DeclarationFormValues = {
  signFullNameFirst: "",
  signDateFirst: todayString,
  signCheckFirst: false,

  signFullNameSecond: "",
  signDateSecond: todayString,
  signCheckSecond: false,
};

const validationSchema = Yup.object().shape({
  signFullNameFirst: Yup.string().required("Required"),
  signCheckFirst: Yup.boolean().oneOf([true], "You must sign (check the box)"),
  signFullNameSecond: Yup.string().required("Required"),
  signCheckSecond: Yup.boolean().oneOf([true], "You must sign (check the box)"),
});

const CompanyInformationForm6 = () => {
  const navigate = useNavigate();
  const newUser = useAppSelector((state) => state.register.newUser);

  if (!newUser) {
    navigate("/signin");
  }

  const onSubmit = (values: DeclarationFormValues) => {
    const latestData = JSON.parse(localStorage.getItem("form3_json") || "{}");
    const mergedData = {
      ...latestData,
      signFullNameFirst: values.signFullNameFirst,
      signFullNameSecond: values.signFullNameSecond,
    };
    localStorage.setItem("form3_json", JSON.stringify(mergedData));
    navigate("/sign-up");
  };

  const {
    handleSubmit,
    getFieldProps,
    errors,
    touched,
    isSubmitting,
    setFieldValue,
    values,
  } = useFormik<DeclarationFormValues>({
    initialValues,
    validationSchema,
    onSubmit,
    enableReinitialize: true,
  });

  const errorText = (fieldName: keyof DeclarationFormValues) =>
    touched[fieldName] && errors[fieldName]
      ? (errors[fieldName] as string)
      : "";

  return (
    <Box display="flex" justifyContent="center" sx={{ mt: 4 }}>
      <Container maxWidth="md">
        <form onSubmit={handleSubmit}>
          <Card
            elevation={24}
            component={motion.div}
            layoutId="declaration-card"
          >
            <CardContent>
              <Stack spacing={4}>
                <Typography variant="h5" fontWeight="600">
                  DECLARATION
                </Typography>

                <Typography variant="body1" paragraph>
                  By signing this questionnaire, I confirm that:
                </Typography>
                <Typography variant="body2">
                  1) The information provided in this questionnaire is accurate,
                  complete, and up-to-date. I undertake to promptly notify the
                  Company in writing of any changes to the submitted
                  information;
                </Typography>
                <Typography variant="body2">
                  2) I agree to use the services provided by the Company
                  exclusively for legitimate purposes;
                </Typography>
                <Typography variant="body2">
                  3) I acknowledge that the Company reserves the right to
                  request additional information or documents related to the
                  submitted information, and I commit to providing them within
                  the time frame specified by the Company;
                </Typography>
                <Typography variant="body2">
                  4) I have read, understood, and accepted the terms of the
                  Company’s Privacy Policy, available at iLedgends.io, including
                  the purposes and legal basis of personal data processing, the
                  involvement of data processors and recipients, the exercise of
                  data subject rights, and the retention period of personal
                  data;
                </Typography>
                <Typography variant="body2">
                  5) I understand that the controller of the personal data
                  processed under this questionnaire is iLedgends s.r.o.;
                </Typography>
                <Typography variant="body2">
                  6) I confirm that the personal data provided in this
                  questionnaire has been lawfully obtained, and I have the right
                  to disclose such data to the Company for the purpose of
                  applying for the requested service(s);
                </Typography>

                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Typography sx={{ fontWeight: 600, mb: 1 }}>
                      First Signatory
                    </Typography>
                    <TextField
                      label="Full Name"
                      fullWidth
                      {...getFieldProps("signFullNameFirst")}
                      error={!!errorText("signFullNameFirst")}
                      helperText={errorText("signFullNameFirst")}
                    />
                    <TextField
                      label="Date"
                      fullWidth
                      disabled
                      value={values.signDateFirst}
                      sx={{ mt: 2 }}
                    />
                    <FormControlLabel
                      sx={{ mt: 1 }}
                      label="Signature"
                      control={
                        <Checkbox
                          checked={values.signCheckFirst}
                          onChange={(e) =>
                            setFieldValue("signCheckFirst", e.target.checked)
                          }
                        />
                      }
                    />
                    {errorText("signCheckFirst") && (
                      <Typography variant="caption" color="error">
                        {errorText("signCheckFirst")}
                      </Typography>
                    )}
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Typography sx={{ fontWeight: 600, mb: 1 }}>
                      Second Signatory
                    </Typography>
                    <TextField
                      label="Full Name"
                      fullWidth
                      {...getFieldProps("signFullNameSecond")}
                      error={!!errorText("signFullNameSecond")}
                      helperText={errorText("signFullNameSecond")}
                    />
                    <TextField
                      label="Date"
                      fullWidth
                      disabled
                      value={values.signDateSecond}
                      sx={{ mt: 2 }}
                    />
                    <FormControlLabel
                      sx={{ mt: 1 }}
                      label="Signature"
                      control={
                        <Checkbox
                          checked={values.signCheckSecond}
                          onChange={(e) =>
                            setFieldValue("signCheckSecond", e.target.checked)
                          }
                        />
                      }
                    />
                    {errorText("signCheckSecond") && (
                      <Typography variant="caption" color="error">
                        {errorText("signCheckSecond")}
                      </Typography>
                    )}
                  </Grid>
                </Grid>

                <Stack spacing={2} mt={3}>
                  <LoadingButton
                    loading={isSubmitting}
                    type="submit"
                    variant="contained"
                  >
                    Save &amp; Finish
                  </LoadingButton>
                </Stack>
              </Stack>
            </CardContent>
          </Card>
        </form>
      </Container>
    </Box>
  );
};

export default CompanyInformationForm6;
